/**
 * Consent Manager
 *
 * Manages analytics platform initialization based on Osano consent status.
 * All analytics platforms should be registered with this manager to ensure
 * they only load when the user has provided appropriate consent.
 *
 * This manager uses Osano's pre-load pattern to set up listeners before
 * Osano.js loads, ensuring we never miss consent events.
 *
 * Docs: https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#-pre-load-and-windowosano
 *
 * Usage:
 *   import consentManager from './consent_manager'
 *   consentManager.register('amplitude', () => import('./analytics/amplitude'))
 */

class ConsentManager {
  constructor() {
    this.platforms = new Map();
    this.initialized = false;
    this.analyticsConsent = false;
    this.consentReceived = false;
  }

  /**
   * Register an analytics platform with the consent manager
   *
   * @param {string} name - Platform name (e.g., 'amplitude', 'snowplow')
   * @param {Function} initFn - Function to initialize the platform (can return a Promise)
   * @param {Object} options - Configuration options
   * @param {boolean} options.essential - If true, loads regardless of consent (default: false)
   */
  register(name, initFn, options = {}) {
    this.platforms.set(name, {
      initFn,
      essential: options.essential || false,
      initialized: false,
    });

    // If already initialized and has consent, initialize this platform immediately
    if (this.initialized && (this.analyticsConsent || options.essential)) {
      this._initializePlatform(name);
    }
  }

  /**
   * Initialize the consent manager
   * Should be called once when the application loads
   *
   * This sets up Osano event listeners using the pre-load pattern,
   * which queues calls before Osano.js is loaded.
   */
  initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Set up the Osano pre-load queue if not already present
    this._setupOsanoPreload();

    // Listen for Osano initialization with consent data
    window.Osano('onInitialized', (consent) => {
      this.consentReceived = true;
      this._handleConsentUpdate(consent);
    });

    // Listen for consent changes after initialization
    window.Osano('onConsentChanged', (consent) => {
      this.consentReceived = true;
      this._handleConsentUpdate(consent);
    });

    // Fallback: If Osano is already initialized, check consent immediately
    // This handles cases where consent manager loads after Osano
    if (window.Osano?.cm) {
      setTimeout(() => {
        if (!this.consentReceived) {
          try {
            const consent = window.Osano.cm.getConsent();
            this._handleConsentUpdate(consent);
          } catch (error) {
            console.error('Error checking Osano consent:', error);
            this._initializeEssentialPlatforms();
          }
        }
      }, 100);
    }

    // Timeout fallback: If no consent received after 10 seconds, initialize essential only
    setTimeout(() => {
      if (!this.consentReceived) {
        console.warn('Osano consent not received within timeout. Initializing essential platforms only.');
        this._initializeEssentialPlatforms();
      }
    }, 10000);
  }

  /**
   * Set up Osano pre-load queue
   * This allows us to call window.Osano() before the script loads
   *
   * @private
   */
  _setupOsanoPreload() {
    if (window.Osano) {
      return; // Already set up
    }

    (function (w, o, d) {
      w[o] = w[o] || function () {
        w[o][d].push(arguments);
      };
      w[o][d] = w[o][d] || [];
    })(window, 'Osano', 'data');
  }

  /**
   * Handle consent updates from Osano
   * @private
   */
  _handleConsentUpdate(consent) {
    if (!consent) {
      this._initializeEssentialPlatforms();
      return;
    }

    // Check if analytics consent is granted
    const hasAnalyticsConsent = consent.ANALYTICS === 'ACCEPT';
    const consentChanged = hasAnalyticsConsent !== this.analyticsConsent;

    this.analyticsConsent = hasAnalyticsConsent;

    if (hasAnalyticsConsent) {
      this._initializeAllPlatforms();
    } else if (consentChanged) {
      // Consent was revoked
      console.log('Analytics consent revoked. Note: Already-loaded platforms cannot be unloaded.');
      this._initializeEssentialPlatforms();
    } else {
      // No consent yet
      this._initializeEssentialPlatforms();
    }
  }

  /**
   * Initialize all registered platforms
   * @private
   */
  _initializeAllPlatforms() {
    this.platforms.forEach((platform, name) => {
      if (!platform.initialized) {
        this._initializePlatform(name);
      }
    });
  }

  /**
   * Initialize only essential platforms (those that don't require consent)
   * @private
   */
  _initializeEssentialPlatforms() {
    this.platforms.forEach((platform, name) => {
      if (platform.essential && !platform.initialized) {
        this._initializePlatform(name);
      }
    });
  }

  /**
   * Initialize a specific platform
   * @private
   */
  _initializePlatform(name) {
    const platform = this.platforms.get(name);
    if (!platform || platform.initialized || platform.initializing) {
      return;
    }

    // Check if platform should be initialized
    if (!platform.essential && !this.analyticsConsent) {
      return;
    }

    platform.initializing = true;

    try {
      const result = platform.initFn();
      if (result?.then) {
        // Handle async initialization
        result
          .then(() => {
            platform.initialized = true;
            platform.initializing = false;
          })
          .catch((error) => {
            console.error(`Error initializing ${name}:`, error);
            platform.initialized = false;
            platform.initializing = false;
          });
      } else {
        // Synchronous initialization succeeded
        platform.initialized = true;
        platform.initializing = false;
      }
    } catch (error) {
      console.error(`Error initializing ${name}:`, error);
      platform.initialized = false;
      platform.initializing = false;
    }
  }

  /**
   * Check if a specific platform has been initialized
   *
   * @param {string} name - Platform name
   * @returns {boolean}
   */
  isInitialized(name) {
    return this.platforms.get(name)?.initialized || false;
  }

  /**
   * Check if analytics consent has been granted
   *
   * @returns {boolean}
   */
  hasAnalyticsConsent() {
    return this.analyticsConsent;
  }
}

// Export singleton instance
const consentManager = new ConsentManager();
export default consentManager;
